// Shared helpers for rendering activities on the Home dashboard

import { StrongifierCompletedActivity } from "src/models/strongifier-completed-activity";

/** Structural subset shared by v1 activities and v2 workouts */
export interface WorkoutLike {
  activity_type: string;
  activity_datetime: string;
  local_activity_datetime?: string | null;
  duration_minutes: number;
  distance_km: number;
}

export interface ActivityVisual {
  iconPath: string;
  color: string;
  ringColor: string;
}

const ICONS: Record<string, string> = {
  run: "M15.5 4a1.6 1.6 0 1 1-3.2 0 1.6 1.6 0 0 1 3.2 0M12.8 6.8 10.8 12M12.8 7.4l3.6 1 2.4-1.6M12.8 7.4 9.4 9.2M10.8 12l3.4 2.6-1 4.6M10.8 12l-2.4 3.6-3.6 1.2",
  ride: "M22 17.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0M9 17.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0M16 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0M12 17.5V14l-3-3 4-3 2 3h2",
  swim: "M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1",
  strength: "M2 12h2M20 12h2M6 8v8M18 8v8M9 6v12M15 6v12M9 12h6",
};

export function getActivityVisual(activityType: string): ActivityVisual {
  const type = (activityType || "").toLowerCase();
  if (type.includes("run")) {
    return { iconPath: ICONS.run, color: "#f0862f", ringColor: "rgba(242,144,58,.55)" };
  }
  if (type.includes("ride") || type.includes("cycl") || type.includes("bike")) {
    return { iconPath: ICONS.ride, color: "#5c6cfa", ringColor: "rgba(92,108,250,.45)" };
  }
  if (type.includes("swim")) {
    return { iconPath: ICONS.swim, color: "#3ec9a7", ringColor: "rgba(62,201,167,.45)" };
  }
  return { iconPath: ICONS.strength, color: "#b183f0", ringColor: "rgba(177,131,240,.45)" };
}

export function getActivityDisplayName(activity: StrongifierCompletedActivity): string {
  return (
    activity.strongifier_activity_name ||
    activity.strava_activity_name ||
    activity.name ||
    activity.activity_type ||
    "Activity"
  );
}

/** "Tue, Jul 29" from the activity's local datetime */
export function formatActivityDate(activity: WorkoutLike): string {
  const iso = activity.local_activity_datetime || activity.activity_datetime;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** Minutes → "52:14" or "1:47:32" */
export function formatDuration(durationMinutes: number): string {
  const totalSeconds = Math.round(durationMinutes * 60);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Minutes and km → "6:22 /km" */
export function formatPace(durationMinutes: number, distanceKm: number): string {
  const totalSeconds = Math.round((durationMinutes / distanceKm) * 60);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")} /km`;
}

export interface ActivityMetric {
  label: string;
  value: string;
}

export function getActivityMetrics(activity: WorkoutLike): ActivityMetric[] {
  const metrics: ActivityMetric[] = [];
  const type = (activity.activity_type || "").toLowerCase();
  const hasDistance = activity.distance_km > 0;

  if (hasDistance) {
    metrics.push({ label: "Distance", value: `${activity.distance_km.toFixed(1)} km` });
  }
  metrics.push({ label: "Time", value: formatDuration(activity.duration_minutes) });
  if (hasDistance && type.includes("run")) {
    metrics.push({
      label: "Pace",
      value: formatPace(activity.duration_minutes, activity.distance_km),
    });
  } else if (hasDistance && activity.duration_minutes > 0) {
    const speed = activity.distance_km / (activity.duration_minutes / 60);
    metrics.push({ label: "Speed", value: `${speed.toFixed(1)} km/h` });
  }
  return metrics;
}
